import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCreateBusinessMemberMutation, useGetBusinessBySubdomainQuery } from '../store';
import OnBoardingCard from '../components/OnBoardingCard';
import Input from '../components/atoms/Input';
import PhotoUpload from '../components/PhotoUpload';
import Button from '../components/atoms/Button';
import CustomModal from '../components/atoms/CustomModal';
import { argentinePhoneRegex } from '../utils/regexs';
import getSubdomain from '../utils/getSubdomain';
import LoadingLottie from '../components/atoms/LoadingLottie';

interface FormData {
  name: string;
  tax_number: string;
  birth_date: string;
  phone_number: string;
  email: string;
  photo_url: string;
  referral_name: string | undefined;
  socials: string | undefined;
}

const schema = yup.object().shape({
  name: yup.string().required('El nombre es requerido'),
  tax_number: yup.string().required('El DNI es requerido').test('is-number', 'Debes ingresar un DNI válido', (value) => {
    if (!value) {
      return true;
    }
    return /^\d+$/.test(value);
  }),
  birth_date: yup.string().required('La fecha de nacimiento es requerida'),
  phone_number: yup.string()
    .required('Debes ingresar un número de teléfono')
    .matches(argentinePhoneRegex, 'Ingresa un número de teléfono argentino válido')
    .test('is-number', 'Debes ingresar un número de teléfono válido', (value) => {
      if (!value) {
        return true;
      }
      return /^\d+$/.test(value);
    }),
  email: yup.string().required('El email es requerido').email('El email no es válido'),
  photo_url: yup.string().required('La foto es requerida'),
  referral_name: yup.string().optional(),
  socials: yup.string().optional(),
});

const MemberRequestPage = () => {
  const subdomain = getSubdomain();
  const { data: business, isLoading } = useGetBusinessBySubdomainQuery(subdomain);
  const navigate = useNavigate();
  const [createBusinessMember, { isLoading: isCreatingMember, isSuccess: isCreatingMemberSuccess }] = useCreateBusinessMemberMutation();
  const [open, setOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    register, handleSubmit, formState: { errors }, setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isCreatingMemberSuccess) {
      setIsModalOpen(true);
    }
  }, [isCreatingMemberSuccess]);

  const onSubmit = (data: FormData) => {
    createBusinessMember({
      businessId: business?.id as string,
      dto: {
        full_name: data.name,
        person_id: data.tax_number,
        birth_date: data.birth_date,
        phone_number: data.phone_number,
        email: data.email,
        status: 'pending',
        photo_url: data.photo_url,
        referral_name: data?.referral_name || undefined,
        socials: data?.socials || undefined,
      },
    });
  };

  const cards = [
    <div className="flex flex-col gap-4 justify-between items-center px-4 text-text-secondary min-h-[60vh] max-h-[60vh] md:min-h-[50vh] md:max-h-[50vh]">
      <img src={business?.logo} alt="De La Ostia" className="w-40 mt-8" />
      <div className="flex flex-row gap-6 justify-center items-center">
        <img src="/music-note-01.svg" alt="" />
        <img src="/lucide-lab_burger.svg" alt="" />
        <img src="/tabler_masks-theater.svg" alt="" />
      </div>
      <div className="flex flex-col gap-4 justify-center items-center mb-8">
        <h1 className="text-3xl font-bold text-center">¡Te invitamos a ser parte de {business?.name}!</h1>
        <p className="text-2xl text-center">Envianos tu solicitud sin cargo</p>
      </div>
    </div>,
    <div className="min-h-[60vh] max-h-[60vh] md:min-h-[50vh] md:max-h-[50vh] flex flex-col justify-between">
      <div>
        <img src={business?.logo} alt="De La Ostia" className="w-28 mt-8 mx-auto" />
      </div>
      <div className="flex flex-col gap-4 justify-between items-center px-4 text-text-secondary">
        <div className="flex flex-col gap-4 justify-center items-center mt-8">
          <h1 className="text-3xl text-center">Socio activo</h1>
          <p className="text-xl text-center">Cada socio tendrá acceso a beneficios como:</p>
        </div>
        <div className="w-full flex justify-center">
          <img src="/onBoarding-1.svg" alt="" className="max-w-full h-auto max-h-[30vh]" />
        </div>
      </div>
    </div>,
    <div className="min-h-[60vh] max-h-[60vh] md:min-h-[50vh] md:max-h-[50vh] flex flex-col justify-between">
      <div>
        <img src={business?.logo} alt="De La Ostia" className="w-28 mt-8 mx-auto" />
      </div>
      <div className="flex flex-col gap-4 justify-between items-center px-4 text-text-secondary">
        <div className="flex flex-col gap-4 justify-center items-center mt-8">
          <h1 className="text-3xl text-center">Ingreso rápido</h1>
          <p className="text-xl text-center">Presentando tu DNI demuestras ser socio y la membresía que posees</p>
        </div>
        <div className="w-full flex justify-center">
          <img src="/onBoarding-3.svg" alt="" className="max-w-full h-auto max-h-[20vh]" />
        </div>
      </div>
    </div>,
    <div className="min-h-[60vh] max-h-[60vh] md:min-h-[50vh] md:max-h-[50vh] flex flex-col justify-between">
      <div>
        <img src={business?.logo} alt="De La Ostia" className="w-28 mt-8 mx-auto" />
      </div>
      <div className="flex flex-col gap-4 justify-between items-center px-4 text-text-secondary">
        <div className="flex flex-col gap-4 justify-center items-center mt-8">
          <h1 className="text-3xl text-center">Consumo</h1>
          <p className="text-xl text-center">Podrás obtener tu tarjeta de consumo bonificado</p>
        </div>
        <div className="w-full flex justify-center">
          <img src="/onBoarding-2.svg" alt="" className="max-w-full h-auto max-h-[20vh]" />
        </div>
      </div>
    </div>,
    <div className="min-h-[60vh] max-h-[60vh] md:min-h-[50vh] md:max-h-[50vh] flex flex-col justify-between">
      <div>
        <img src={business?.logo} alt="De La Ostia" className="w-28 mt-8 mx-auto" />
      </div>
      <div className="flex flex-col gap-4 justify-between items-center px-4 text-text-secondary">
        <div className="flex flex-col gap-4 justify-center items-center mt-8">
          <h1 className="text-3xl text-center">Beneficios exclusivos</h1>
          <p className="text-xl text-center">Accederás a descuentos y promociones para tu cumpleaños o eventos empresariales</p>
        </div>
        <div className="w-full flex justify-center">
          <img src="/gift.png" alt="" className="max-w-full h-auto max-h-[20vh]" />
        </div>
      </div>
    </div>,

  ];

  if (isLoading) {
    return <LoadingLottie />;
  }

  return (
    <div>
      <OnBoardingCard cards={cards} open={open} mainButton={{
        title: 'Continuar',
        onClick: () => { setOpen(false); },
      }} />
      <div className="flex flex-col my-6 px-4">
        <h1>Datos personales</h1>
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-8 mt-2'>
          <div className='flex flex-col gap-4'>
            <Input
              id="name"
              label="Nombre y Apellido *"
              labelAbove
              {...register('name')}
              errMsg={errors.name?.message}
            />
            <div>
              <Input
                id="tax_number"
                label="DNI *"
                labelAbove
                {...register('tax_number')}
                errMsg={errors.tax_number?.message}
                type="number"
              />
              <p className='text-sm text-text-tertiary'>Sin puntos, revisar detenidamente que sea correcto</p>
            </div>
            <Input
              id="birth_date"
              label="Fecha de nacimiento *"
              type="date"
              labelAbove
              {...register('birth_date')}
              errMsg={errors.birth_date?.message}
            />
            <div>
              <label className="text-left font-sm font-medium mb-2">
                Foto de socio
              </label>
              <PhotoUpload onFileUploaded={(url) => setValue('photo_url', url)} />
              <p className='text-sm text-text-tertiary'>Foto con buena luminosidad y sin elementos que tapen la cara (barbijo, lentes, gorras, etc)</p>
              {errors.photo_url && <p className='text-sm text-red-500'>{errors.photo_url.message}</p>}
            </div>
          </div>
          <div>
            <h1>Datos de contacto</h1>
            <div className='flex flex-col gap-4 mt-2'>
              <Input
                id="phone_number"
                label="Número de WhatsApp *"
                labelAbove
                {...register('phone_number')}
                errMsg={errors.phone_number?.message}
                type="number"
              />
              <Input
                id="email"
                label="Email *"
                labelAbove
                {...register('email')}
                errMsg={errors.email?.message}
                type="email"
              />
            </div>
          </div>
          <div>
            <h1>Datos adicionales (opcional)</h1>
            <p>Completa los siguientes datos para poder obtener un mejor servicio.</p>
            <div className='flex flex-col gap-4 mt-2'>
              <Input
                id='referral_name'
                label='¿Quién te sugirió ser parte de este club?'
                placeholder='Ingresá Nombre y Apellido'
                labelAbove
                {...register('referral_name')}
                errMsg={errors.referral_name?.message}
              />
              <Input
                id='socials'
                label='Redes sociales'
                placeholder='Ingresá tu Facebook o Instagram'
                labelAbove
                {...register('socials')}
                errMsg={errors.socials?.message}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              type="submit"
              loading={isCreatingMember}>
              Enviar
            </Button>
            <Button onClick={() => navigate(-1)}
              variant="secondary">
              Cancelar
            </Button>
          </div>
        </form>
      </div>
      <CustomModal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} hideCancelButton>
        <div className='max-w-[400px] flex flex-col gap-4'>
          <h1>¡Solicitud de socio generada con éxito!</h1>
          <p>Tu solicitud fue enviada con éxito al productor del evento. Te mantendremos informado por mail ante cualquier novedad o actualización.</p>
          <div className='mt-2'>
            <Button variant="primary" onClick={() => {
              setIsModalOpen(false);
              navigate('/');
            }}>Aceptar</Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default MemberRequestPage;
